function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * List View Utilities
 * Generic utilities for rendering form data in list view format
 */

/**
 * Get field label from schema
 */
export const getFieldLabel = (fieldId, schema) => {
  if (!schema?.fields) return fieldId;
  const field = schema.fields.find(f => f.id === fieldId);
  return field?.label || field?.name || fieldId;
};

/**
 * Format field value for display
 * @param {*} value - The value to format
 * @param {Object} field - Optional field definition for type-specific formatting
 * @param {Object} formData - Optional form data object (needed for price field currency)
 * @returns {string} - Formatted value string
 */
export const formatFieldValue = function (value) {
  let field = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  let formData = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  const tp = field?.type !== undefined && field?.type !== null ? String(field.type).toLowerCase().trim() : '';

  // Handle currency fields FIRST - they may have field.currency as fallback even if value is empty
  if (tp === 'currency') {
    // Use field.currency as fallback if value is empty/undefined (for disabled/default values)
    const currencyValue = value || field?.currency || '';

    // Currency field now stores a currency code string, not a number
    if (typeof currencyValue === 'string' && currencyValue.trim() !== '') {
      try {
        const {
          CURRENCIES
        } = require('../components/FormBuilder/PropertiesPanel/CurrenciesList');
        const currency = CURRENCIES.find(c => c.code === currencyValue);
        if (currency) {
          return `${currency.code} - ${currency.name} (${currency.symbol})`;
        }
        // Fallback if currency not found
        return currencyValue;
      } catch (e) {
        // Fallback if CURRENCIES not available
        return currencyValue;
      }
    }
    // Legacy support: if value is a number, treat it as old format
    if (typeof value === 'number' || typeof value === 'string' && !isNaN(value)) {
      const currency = field.currency || 'USD';
      const locale = field.locale || 'en-US';
      const num = parseFloat(value);
      if (!isNaN(num)) {
        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency
          }).format(num);
        } catch (e) {
          return `${currency} ${num.toFixed(2)}`;
        }
      }
    }
    // If no value but field.currency exists, use it
    if (field?.currency) {
      try {
        const {
          CURRENCIES
        } = require('../components/FormBuilder/PropertiesPanel/CurrenciesList');
        const currency = CURRENCIES.find(c => c.code === field.currency);
        if (currency) {
          return `${currency.code} - ${currency.name} (${currency.symbol})`;
        }
        return field.currency;
      } catch (e) {
        return field.currency;
      }
    }
    // If no value and no field.currency, return empty string (will be formatted as '-' by caller)
    return '';
  }

  // Check for empty values AFTER currency handling (currency may have field.currency fallback)
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  // Handle phone number objects {countryCode, number}
  if ((tp === 'tel' || tp === 'phone') && typeof value === 'object' && value !== null) {
    if (value.countryCode && value.number) {
      // Import COUNTRY_CODES dynamically to avoid circular dependencies
      try {
        const {
          COUNTRY_CODES
        } = require('../components/FormBuilder/PropertiesPanel/CountryCodesList');
        const country = COUNTRY_CODES.find(c => c.code === value.countryCode);
        const dialCode = country?.dialCode || '';
        return `${dialCode} ${value.number}`.trim();
      } catch (e) {
        // Fallback if COUNTRY_CODES not available
        return value.number || '-';
      }
    }
    return value.number || '-';
  }

  // Handle price fields - value is numeric, currency is stored in formData[field.id + '_currency']
  if (tp === 'price' && (typeof value === 'number' || typeof value === 'string' && !isNaN(value))) {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      let currencySymbol = field.currencySymbol || '$';
      let currencyCode = 'USD';

      // Try to get currency from formData if available
      if (formData && field.id) {
        const currencyKey = `${field.id}_currency`;
        const storedCurrencyCode = formData[currencyKey];
        if (storedCurrencyCode) {
          try {
            const {
              CURRENCIES
            } = require('../components/FormBuilder/PropertiesPanel/CurrenciesList');
            const currency = CURRENCIES.find(c => c.code === storedCurrencyCode);
            if (currency) {
              currencySymbol = currency.symbol;
              currencyCode = currency.code;
            }
          } catch (e) {
            // Fallback to field.currencySymbol if CURRENCIES not available
          }
        }
      }
      const decimalPlaces = field.decimalPlaces !== undefined ? field.decimalPlaces : 2;
      return `${currencySymbol}${num.toFixed(decimalPlaces)}`;
    }
  }

  // Price as { amount, currency } (nested saves / some renderers)
  if (tp === 'price' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const amount = value.amount ?? value.value;
    let code = value.currency ?? value.currencyCode ?? value.code ?? '';
    if (!code && formData && field.id) {
      const currencyKey = `${field.id}_currency`;
      code = formData[currencyKey] ?? formData[String(currencyKey)] ?? '';
    }
    const decimalPlaces = field.decimalPlaces !== undefined ? field.decimalPlaces : 2;
    if (amount !== undefined && amount !== null && String(amount).trim() !== '') {
      const num = typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/,/g, ''));
      if (!Number.isNaN(num)) {
        let currencySymbol = field.currencySymbol || '';
        if (code) {
          try {
            const {
              CURRENCIES
            } = require('../components/FormBuilder/PropertiesPanel/CurrenciesList');
            const cur = CURRENCIES.find(c => c.code === code);
            if (cur) {
              currencySymbol = cur.symbol;
            }
          } catch (_e) {
            // ignore
          }
        }
        const symPrefix = currencySymbol ? `${currencySymbol}` : '';
        const fixed = num.toFixed(decimalPlaces);
        const codeSuffix = code ? ` ${code}` : '';
        return `${symPrefix}${fixed}${codeSuffix}`.trim();
      }
      return `${code} ${amount}`.trim();
    }
    if (code) {
      return String(code);
    }
  }

  // Handle repeater fields (arrays of objects)
  if ((tp === 'repeater' || tp === 'repeatable') && Array.isArray(value)) {
    if (value.length === 0) {
      return '-';
    }
    return `${value.length} item${value.length !== 1 ? 's' : ''}`;
  }

  // Handle tabs fields - return a summary (fields inside tabs are rendered separately)
  if (tp === 'tabs') {
    const tabs = field.tabs || [];
    const tabContent = field.tabContent || {};
    const totalFields = Object.values(tabContent).flat().filter(item => item.type === 'field').length;
    if (totalFields === 0) {
      return '-';
    }
    return `${tabs.length} tab${tabs.length !== 1 ? 's' : ''} with ${totalFields} field${totalFields !== 1 ? 's' : ''}`;
  }

  // Handle tags (arrays)
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  // Handle other objects (fallback to JSON)
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

/**
 * Check if a field belongs to a container
 */
export const getFieldContainer = (fieldId, allFields) => {
  for (const f of allFields) {
    const containerTypes = ['tabs', 'table'];
    if (containerTypes.includes(f.type)) {
      // For table, check sections (legacy) and cells in columns (new structure)
      if (f.type === 'table') {
        const containerFieldIds = f.sections;
        if (containerFieldIds && Array.isArray(containerFieldIds) && containerFieldIds.includes(fieldId)) {
          return f.id;
        }
        // Check cells in columns
        const columns = f.columns || [];
        for (const col of columns) {
          const cells = col.cells || {};
          const cellValues = Object.values(cells);
          const hasField = cellValues.some(cell => cell && cell.type === 'field' && cell.value === fieldId);
          if (hasField) {
            return f.id;
          }
        }
      }
      // For tabs, check tabContent (which contains field IDs)
      if (f.type === 'tabs' && f.tabContent) {
        const tabContent = f.tabContent;
        const allFieldIds = Object.values(tabContent).flat().filter(item => item.type === 'field').map(item => item.value);
        if (allFieldIds.includes(fieldId)) {
          return f.id;
        }
      }
    }
  }
  return null;
};

/**
 * Get all fields that belong to a tabs container
 * Returns an array of field objects organized by tab
 */
export const getTabsFields = (tabsField, allFields) => {
  if (!tabsField || tabsField.type !== 'tabs') {
    return [];
  }
  const tabs = tabsField.tabs || [];
  const tabContent = tabsField.tabContent || {};
  const result = [];
  tabs.forEach(tab => {
    const tabItems = tabContent[tab.id] || [];
    const tabFieldIds = tabItems.filter(item => item.type === 'field').map(item => item.value);
    tabFieldIds.forEach(fieldId => {
      const field = allFields.find(f => f.id === fieldId);
      if (field) {
        result.push(_objectSpread(_objectSpread({}, field), {}, {
          _tabId: tab.id,
          _tabLabel: tab.label
        }));
      }
    });
  });
  return result;
};

/**
 * Get all fields that belong to a table container
 * Returns an array of field objects organized by column and row
 */
export const getTableFields = (tableField, allFields) => {
  if (!tableField || tableField.type !== 'table') {
    return [];
  }
  const columns = tableField.columns || [];
  const rows = tableField.rows || [];
  const result = [];
  columns.forEach(col => {
    const cells = col.cells || {};
    rows.forEach(row => {
      const cellConfig = cells[row.id];
      if (cellConfig && cellConfig.type === 'field') {
        const field = allFields.find(f => f.id === cellConfig.value);
        if (field) {
          result.push(_objectSpread(_objectSpread({}, field), {}, {
            _columnKey: col.key,
            _columnLabel: col.label,
            _rowId: row.id
          }));
        }
      }
    });
  });
  return result;
};

/**
 * Check if field type should be skipped in list view
 */
export const shouldSkipInListView = fieldType => {
  const containerTypes = ['divider'
  // 'hidden' fields are now shown in list view
  // 'tabs' and 'table' are now rendered with their content, so don't skip them
  ];
  return containerTypes.includes(fieldType);
};

/**
 * Format select field value with option labels
 */
export const formatSelectValue = (value, field) => {
  if (!field?.options || !Array.isArray(field.options)) {
    return formatFieldValue(value);
  }
  if (field.multiselect && Array.isArray(value)) {
    if (value.length === 0) {
      return '-';
    }
    const selectedLabels = value.map(val => {
      const option = field.options.find(opt => opt.value === val);
      return option?.label || val;
    }).filter(Boolean);
    return selectedLabels.join(', ');
  }
  const option = field.options.find(opt => opt.value === value);
  return option ? option.label : formatFieldValue(value);
};

/**
 * Check if a field type is a file-related field
 */
export const isFileFieldType = fieldType => {
  if (!fieldType) return false;
  const fileFieldTypes = ['file', 'attachment', 'image', 'file-upload', 'fileupload', 'image-upload', 'imageupload'];
  const normalizedType = String(fieldType).toLowerCase().trim();
  return fileFieldTypes.includes(normalizedType) || fileFieldTypes.includes(fieldType);
};

/**
 * Process form data for initialization
 * Resets file fields to empty arrays and preserves other field values
 */
export const processFormDataForInitialization = (formData, schema) => {
  if (!formData || !schema?.fields) {
    return formData || {};
  }
  const processedData = _objectSpread({}, formData);
  schema.fields.forEach(field => {
    const value = processedData[field.id];
    if (!value) return;

    // FileUpload needs File objects (not filename strings)
    // Reset file fields to empty arrays for proper initialization
    if (isFileFieldType(field.type)) {
      processedData[field.id] = [];
    }
  });
  return processedData;
};

/**
 * Parse filled form value from string or object
 */
export const parseFilledFormValue = filledFormValue => {
  if (!filledFormValue) return {};
  try {
    if (typeof filledFormValue === 'string') {
      return JSON.parse(filledFormValue);
    }
    return filledFormValue;
  } catch (error) {
    console.error('Error parsing filled form value:', error);
    return {};
  }
};

/**
 * Extract subform data from parsed form value
 * Handles both array and object data structures
 */
export const extractSubformData = function (parsedFormValue, subformId) {
  let index = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  if (!parsedFormValue) return {};
  if (Array.isArray(parsedFormValue)) {
    // Array format: [{ subformId: data }, ...]
    if (index !== null && index >= 0) {
      return parsedFormValue[index]?.[subformId] || {};
    }
    // Find first entry with this subformId
    const entry = parsedFormValue.find(item => item[subformId]);
    return entry?.[subformId] || {};
  }

  // Object format: { subformId: data, ... }
  return parsedFormValue[subformId] || {};
};